import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Server, Database, Activity,
  AlertTriangle, CheckCircle, XCircle, Clock, Users,
  Cpu, HardDrive, Zap, Settings, Code2, Play,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

// Consultas predefinidas para problemas comunes
const QUERIES_PRESET = [
  {
    nombre: "Reservas sin zona asignada",
    sql: "SELECT id, nombre_cliente, fecha_reserva, hora_reserva, estado FROM crm_reservas WHERE zona_id IS NULL AND estado NOT IN ('cancelada','no_show') ORDER BY fecha_reserva DESC LIMIT 20",
  },
  {
    nombre: "Reservas duplicadas (mismo cliente + fecha + hora)",
    sql: "SELECT nombre_cliente, telefono, fecha_reserva, hora_reserva, COUNT(*) as duplicados FROM crm_reservas WHERE estado NOT IN ('cancelada','no_show') GROUP BY nombre_cliente, telefono, fecha_reserva, hora_reserva HAVING COUNT(*) > 1 ORDER BY duplicados DESC",
  },
  {
    nombre: "Usuarios sin último login (posible cuenta muerta)",
    sql: "SELECT full_name, email, role, created_at FROM crm_users WHERE is_active = TRUE AND last_login_at IS NULL ORDER BY created_at DESC",
  },
  {
    nombre: "Reservas pendientes vencidas (no confirmadas a tiempo)",
    sql: "SELECT id, nombre_cliente, telefono, fecha_reserva, hora_reserva, sucursal_id FROM crm_reservas WHERE estado='pendiente' AND created_at < NOW() - INTERVAL '48 hours' ORDER BY created_at ASC LIMIT 30",
  },
  {
    nombre: "Pistas/Mesas con capacidad 0",
    sql: "SELECT id, num_mesa, sala_nombre, reservable_online, capacidad FROM crm_mesas WHERE COALESCE(capacidad,0) = 0 AND activa = TRUE ORDER BY sala_nombre, num_mesa",
  },
  {
    nombre: "Tareas operativas vencidas sin completar",
    sql: "SELECT id, titulo, creado_por_nombre, fecha_limite, created_at FROM crm_tareas_operativas WHERE estado='pendiente' AND fecha_limite < NOW() ORDER BY fecha_limite ASC",
  },
  {
    nombre: "Zonas sin pistas/mesas configuradas",
    sql: "SELECT z.id, z.nombre, z.permite_seleccion_mesa FROM crm_zonas z WHERE z.activa=TRUE AND z.permite_seleccion_mesa=TRUE AND NOT EXISTS (SELECT 1 FROM crm_mesas m WHERE LOWER(m.sala_nombre)=LOWER(z.nombre))",
  },
  {
    nombre: "Reservas con pista/mesa asignada (recurso_id)",
    sql: "SELECT r.id, r.nombre_cliente, r.fecha_reserva, r.hora_reserva, r.recurso_id, r.duracion_minutos, r.estado FROM crm_reservas r WHERE r.recurso_id IS NOT NULL ORDER BY r.fecha_reserva DESC, r.hora_reserva LIMIT 30",
  },
  {
    nombre: "Inventario: tablas y conteo de registros",
    sql: "SELECT schemaname, relname AS tabla, n_live_tup AS registros FROM pg_stat_user_tables WHERE schemaname='public' ORDER BY n_live_tup DESC",
  },
];

function fmtDate(s?: string) {
  if (!s) return "—";
  return new Date(s).toLocaleString("es-VE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function StatusBadge({ ok, label }: { ok: boolean; label?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
      {ok ? <CheckCircle className="size-3" /> : <XCircle className="size-3" />}
      {label ?? (ok ? "OK" : "Error")}
    </span>
  );
}

const TIPO_COLOR: Record<string, string> = {
  cancelada: "text-red-600 bg-red-50",
  no_show: "text-orange-600 bg-orange-50",
  auto_cancelada: "text-rose-600 bg-rose-50",
  confirmada: "text-emerald-600 bg-emerald-50",
  creada: "text-blue-600 bg-blue-50",
  editada: "text-violet-600 bg-violet-50",
  checkin: "text-indigo-600 bg-indigo-50",
};

export default function DiagnosticosPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [tab, setTab] = useState<"sistema" | "sql">("sistema");
  const [sql, setSql] = useState("SELECT * FROM crm_reservas ORDER BY created_at DESC LIMIT 10");
  const [queryResult, setQueryResult] = useState<any>(null);
  const [queryLoading, setQueryLoading] = useState(false);

  const cargar = () => {
    setLoading(true);
    api<any>("/v1/admin/diagnosticos")
      .then(d => { setData(d); setLastRefresh(new Date()); })
      .catch(e => setData({ error: e.message }))
      .finally(() => setLoading(false));
  };

  const ejecutarQuery = async () => {
    if (!sql.trim()) return;
    setQueryLoading(true);
    setQueryResult(null);
    try {
      const res = await api<any>("/v1/admin/diagnosticos/query", { method: "POST", body: { sql } });
      setQueryResult(res);
    } catch (e: any) {
      setQueryResult({ error: e.message });
    } finally {
      setQueryLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  if (!user?.superAdmin && user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="text-center">
          <AlertTriangle className="size-12 text-red-400 mx-auto mb-3" />
          <p className="text-ink-600">Acceso restringido — solo superAdmin</p>
        </div>
      </div>
    );
  }

  const s = data?.sistema;
  const cfg = data?.configuracion;
  const stats = data?.estadisticas;

  const memPct = s ? Math.round((s.memoriaUsadaMb / s.memoriaTotalMb) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white">
              <ArrowLeft className="size-5" />
            </button>
            <div className="flex items-center gap-2">
              <Activity className="size-5 text-brand-400" />
              <h1 className="font-bold text-white text-lg">Panel de Diagnóstico</h1>
              <span className="text-xs text-slate-500 ml-2">Solo super admin</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Tabs */}
            <div className="flex bg-slate-800 rounded-lg p-0.5 gap-0.5">
              {[
                { id: "sistema", icon: Activity, label: "Sistema" },
                { id: "sql", icon: Code2, label: "Consultas SQL" },
              ].map(t => (
                <button key={t.id} onClick={() => setTab(t.id as any)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === t.id ? "bg-slate-600 text-white" : "text-slate-400 hover:text-white"}`}>
                  <t.icon className="size-3.5" />{t.label}
                </button>
              ))}
            </div>
            <span className="text-xs text-slate-500">
              {lastRefresh.toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
            {tab === "sistema" && (
              <button onClick={cargar} disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg transition-colors disabled:opacity-50">
                <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
                Actualizar
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* ── TAB SQL ── */}
        {tab === "sql" && (
          <div className="space-y-4">
            {/* Consultas preset */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">
                Consultas predefinidas — detección de inconsistencias
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {QUERIES_PRESET.map((q, i) => (
                  <button key={i} onClick={() => setSql(q.sql)}
                    className="text-left px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-slate-300 hover:text-white transition-colors border border-slate-700 hover:border-brand-500">
                    <span className="text-brand-400 font-mono mr-1.5">Q{i+1}.</span>
                    {q.nombre}
                  </button>
                ))}
              </div>
            </div>

            {/* Editor SQL */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                  <Code2 className="size-3.5" /> Editor SQL (solo SELECT)
                </h2>
                <button onClick={ejecutarQuery} disabled={queryLoading || !sql.trim()}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg font-medium disabled:opacity-40 transition-colors">
                  <Play className={`size-3.5 ${queryLoading ? "animate-spin" : ""}`} />
                  {queryLoading ? "Ejecutando…" : "Ejecutar"}
                </button>
              </div>
              <textarea
                className="w-full h-32 bg-slate-800 text-emerald-400 font-mono text-xs p-3 rounded-lg border border-slate-700 focus:border-brand-500 focus:outline-none resize-y"
                value={sql}
                onChange={e => setSql(e.target.value)}
                onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') ejecutarQuery(); }}
                placeholder="SELECT * FROM crm_reservas LIMIT 10;"
                spellCheck={false}
              />
              <p className="text-[10px] text-slate-600">Solo SELECT · máx 100 filas automático · Ctrl+Enter para ejecutar</p>
            </div>

            {/* Resultado */}
            {queryResult && (
              <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                {queryResult.error ? (
                  <div className="p-5 flex items-start gap-3">
                    <XCircle className="size-5 text-red-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-400">Error en la consulta</p>
                      <p className="text-xs text-red-300 mt-1 font-mono">{queryResult.error}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-3">
                      <CheckCircle className="size-4 text-emerald-400" />
                      <span className="text-xs text-slate-300">
                        <strong className="text-white">{queryResult.filas}</strong> filas ·
                        <strong className="text-white ml-1">{queryResult.columnas?.length}</strong> columnas ·
                        <span className="text-slate-500 ml-1">{queryResult.tiempoMs}ms</span>
                      </span>
                    </div>
                    {queryResult.filas === 0 ? (
                      <div className="px-5 py-6 text-center text-slate-500 text-sm">
                        ✅ La consulta no devolvió resultados
                      </div>
                    ) : (
                      <div className="overflow-auto max-h-96">
                        <table className="w-full text-xs min-w-max">
                          <thead className="sticky top-0">
                            <tr className="bg-slate-800 border-b border-slate-700">
                              {queryResult.columnas?.map((col: string) => (
                                <th key={col} className="px-3 py-2 text-left text-slate-400 font-medium whitespace-nowrap">
                                  {col}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800">
                            {queryResult.datos?.map((row: any, i: number) => (
                              <tr key={i} className="hover:bg-slate-800/50">
                                {queryResult.columnas?.map((col: string) => (
                                  <td key={col} className="px-3 py-1.5 text-slate-300 max-w-[200px] truncate"
                                    title={String(row[col] ?? '')}>
                                    {row[col] === null ? <span className="text-slate-600 italic">null</span>
                                      : String(row[col]).length > 50
                                        ? String(row[col]).slice(0, 50) + "…"
                                        : String(row[col])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "sistema" && loading && !data && (
          <div className="flex items-center justify-center h-32 text-slate-400">
            <RefreshCw className="size-5 animate-spin mr-2" /> Cargando diagnósticos…
          </div>
        )}

        {tab === "sistema" && data?.error && (
          <div className="bg-red-900/30 border border-red-700 text-red-300 rounded-xl p-4 flex items-center gap-3">
            <XCircle className="size-5 shrink-0" />
            <p className="text-sm">Error al cargar: {data.error}</p>
          </div>
        )}

        {tab === "sistema" && data && !data.error && (
          <>
            {/* ── Fila 1: Estado del sistema ── */}
            <div>
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Estado del sistema</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { icon: Server,   label: "API",       value: s?.uptimeHumano ?? "—",   sub: `PID ${s?.pid}`,           ok: true },
                  { icon: Cpu,      label: "Memoria",   value: `${s?.memoriaUsadaMb}MB`,  sub: `${memPct}% de ${s?.memoriaTotalMb}MB`, ok: memPct < 85 },
                  { icon: Database, label: "Base datos", value: data.dbSize ?? "—",        sub: `Node ${s?.nodeVersion}`,   ok: true },
                  { icon: Zap,      label: "Entorno",   value: s?.env?.toUpperCase() ?? "—", sub: cfg?.publicUrl?.split("//")[1]?.split("/")[0] ?? "—", ok: s?.env === "production" },
                ].map(({ icon: Icon, label, value, sub, ok }) => (
                  <div key={label} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-slate-400">
                        <Icon className="size-4" />
                        <span className="text-xs">{label}</span>
                      </div>
                      <StatusBadge ok={ok} />
                    </div>
                    <p className="text-lg font-bold text-white">{value}</p>
                    <p className="text-xs text-slate-500 truncate">{sub}</p>
                    {label === "Memoria" && (
                      <div className="mt-2 h-1 bg-slate-800 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${memPct > 80 ? "bg-red-500" : memPct > 60 ? "bg-amber-500" : "bg-emerald-500"}`}
                          style={{ width: `${memPct}%` }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Fila 1b: Red e IP ── */}
            {data.redInfo && (
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "IP Pública", value: data.redInfo.ipPublica, icon: "🌐" },
                  { label: "IP Local (contenedor)", value: data.redInfo.ipLocal, icon: "🔌" },
                  { label: "Disco", value: data.disco ? `${data.disco.usado} usados de ${data.disco.tamano} (${data.disco.usadoPct})` : "N/A", sub: `${data.disco?.libre ?? "?"} libres`, icon: "💾" },
                ].map(({ label, value, sub, icon }) => (
                  <div key={label} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                    <p className="text-xs text-slate-500 mb-1 flex items-center gap-1.5"><span>{icon}</span>{label}</p>
                    <p className="text-sm font-bold text-white">{value}</p>
                    {sub && <p className="text-xs text-slate-500">{sub}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* ── Fila 2: Configuración ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <Settings className="size-3.5" /> Configuración
                </h2>
                <div className="space-y-3">
                  {cfg && [
                    { label: "SMTP", ok: cfg.smtpConfigurado, detail: cfg.smtpUsuario },
                    { label: "ICG (SQL Server)", ok: cfg.icgHabilitado, detail: cfg.icgHabilitado ? "Habilitado" : "Desactivado" },
                    { label: "JWT Secret", ok: cfg.jwtConfigurado, detail: cfg.jwtConfigurado ? "Configurado" : "NO CONFIGURADO ⚠️" },
                    { label: "URL Pública", ok: !!cfg.publicUrl && cfg.publicUrl !== "(no configurado)", detail: cfg.publicUrl },
                    { label: "Host DB", ok: true, detail: cfg.dbHost },
                  ].map(({ label, ok, detail }) => (
                    <div key={label} className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">{label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 max-w-[200px] truncate">{detail}</span>
                        <StatusBadge ok={ok} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Estadísticas */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <HardDrive className="size-3.5" /> Registros en base de datos
                </h2>
                <div className="grid grid-cols-3 gap-3">
                  {stats && [
                    { label: "Reservas", value: stats.reservas ?? 0, color: "text-amber-400" },
                    { label: "Hoy", value: stats.reservasHoy ?? 0, color: "text-emerald-400" },
                    { label: "Pendientes", value: stats.reservasPendientes ?? 0, color: "text-red-400" },
                    { label: "Usuarios", value: stats.usuarios ?? 0, color: "text-blue-400" },
                    { label: "Clientes", value: stats.clientes ?? 0, color: "text-violet-400" },
                    { label: "Eventos", value: stats.eventos_privados ?? stats.eventos ?? 0, color: "text-pink-400" },
                    { label: "Mesas/Pistas", value: stats.mesas ?? 0, color: "text-cyan-400" },
                    { label: "Zonas", value: stats.zonas ?? 0, color: "text-indigo-400" },
                    { label: "Tareas", value: stats.tareas_operativas ?? 0, color: "text-orange-400" },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="bg-slate-800 rounded-lg p-3 text-center">
                      <p className={`text-xl font-bold ${color}`}>{value >= 0 ? value.toLocaleString() : "—"}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>
                {stats?.reservasPorEstado?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-800">
                    <p className="text-xs text-slate-500 mb-2">Reservas últimos 30 días por estado:</p>
                    <div className="flex gap-2 flex-wrap">
                      {stats.reservasPorEstado.map((e: any) => (
                        <span key={e.estado} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${TIPO_COLOR[e.estado] ?? "bg-slate-700 text-slate-300"}`}>
                          {e.estado}: {e.total}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Fila 3: Actividad reciente + Errores ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Errores/Cancelaciones */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
                  <AlertTriangle className="size-4 text-amber-400" />
                  <h2 className="text-xs font-semibold text-slate-300 uppercase tracking-widest">
                    Cancelaciones y no-shows (7 días)
                  </h2>
                  <span className="ml-auto text-xs text-slate-500">{data.erroresRecientes?.length ?? 0}</span>
                </div>
                {data.erroresRecientes?.length === 0 ? (
                  <div className="px-5 py-6 text-center text-slate-500 text-sm">
                    <CheckCircle className="size-6 text-emerald-500 mx-auto mb-2" />
                    Sin cancelaciones en los últimos 7 días
                  </div>
                ) : (
                  <div className="divide-y divide-slate-800 max-h-64 overflow-y-auto">
                    {data.erroresRecientes?.map((e: any, i: number) => (
                      <div key={i} className="px-4 py-2.5 flex items-start gap-3">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${TIPO_COLOR[e.tipo] ?? "bg-slate-700 text-slate-300"}`}>
                          {e.tipo}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-300 truncate">
                            #{e.reservaId} — {e.cliente}
                          </p>
                          {e.notas && <p className="text-[10px] text-slate-500 truncate">{e.notas}</p>}
                        </div>
                        <span className="text-[10px] text-slate-600 shrink-0">{fmtDate(e.fecha)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actividad reciente */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
                  <Clock className="size-4 text-blue-400" />
                  <h2 className="text-xs font-semibold text-slate-300 uppercase tracking-widest">Actividad reciente</h2>
                </div>
                <div className="divide-y divide-slate-800 max-h-64 overflow-y-auto">
                  {data.actividadReciente?.map((a: any, i: number) => (
                    <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${TIPO_COLOR[a.tipo] ?? "bg-slate-700 text-slate-300"}`}>
                        {a.tipo}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-300 truncate">
                          {a.cliente || `Reserva #${a.reservaId}`}
                          {a.sucursal && <span className="text-slate-500 ml-1">· {a.sucursal}</span>}
                        </p>
                        <p className="text-[10px] text-slate-500">{a.usuario ?? "Sistema"} · {a.role ?? ""}</p>
                      </div>
                      <span className="text-[10px] text-slate-600 shrink-0">{fmtDate(a.fecha)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Fila 3b: Sync ICG logs ── */}
            {data.syncLogs?.length > 0 && (
              <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
                  <RefreshCw className="size-4 text-cyan-400" />
                  <h2 className="text-xs font-semibold text-slate-300 uppercase tracking-widest">Sincronización ICG — últimas ejecuciones</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-max">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-800">
                        {["Entidad", "Estado", "Recibidos", "Insertados", "Actualizados", "Duración", "Error", "Iniciada"].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {data.syncLogs.map((s: any, i: number) => (
                        <tr key={i} className={s.estado === 'error' ? "bg-red-900/20 hover:bg-red-900/30" : "hover:bg-slate-800/50"}>
                          <td className="px-3 py-2 font-medium text-slate-200">{s.entidad}</td>
                          <td className="px-3 py-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${s.estado === 'ok' ? 'bg-emerald-900 text-emerald-300' : s.estado === 'error' ? 'bg-red-900 text-red-300' : 'bg-slate-700 text-slate-400'}`}>
                              {s.estado}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-400 tabular-nums">{s.recibidos ?? "—"}</td>
                          <td className="px-3 py-2 text-emerald-400 tabular-nums">{s.insertados ?? "—"}</td>
                          <td className="px-3 py-2 text-blue-400 tabular-nums">{s.actualizados ?? "—"}</td>
                          <td className="px-3 py-2 text-slate-500 tabular-nums">{s.duracionMs ? `${s.duracionMs}ms` : "—"}</td>
                          <td className="px-3 py-2 max-w-[200px]">
                            {s.errorMensaje ? (
                              <span className="text-red-400 truncate block" title={s.errorMensaje}>
                                {s.errorMensaje.slice(0, 60)}{s.errorMensaje.length > 60 ? '…' : ''}
                              </span>
                            ) : <span className="text-slate-600">—</span>}
                          </td>
                          <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDate(s.iniciadaAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Horas pico + Usuarios más activos ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Horas pico */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <Clock className="size-3.5 text-amber-400" /> Horas pico de reservas (últimos 90 días)
                </h2>
                {data.horasPico?.length === 0 ? (
                  <p className="text-slate-600 text-sm">Sin datos suficientes</p>
                ) : (
                  <div className="space-y-1.5">
                    {data.horasPico?.map((h: any) => {
                      const max = Math.max(...(data.horasPico?.map((x: any) => x.total_reservas) ?? [1]));
                      const pct = Math.round((h.total_reservas / max) * 100);
                      return (
                        <div key={h.hora} className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-500 w-10 text-right tabular-nums shrink-0">{String(h.hora).padStart(2, '0')}:00</span>
                          <div className="flex-1 h-4 bg-slate-800 rounded-sm overflow-hidden">
                            <div className={`h-full rounded-sm transition-all ${pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-amber-500' : 'bg-brand-500'}`}
                              style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[10px] text-slate-400 w-8 tabular-nums">{h.total_reservas}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Usuarios más activos */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
                  <Users className="size-4 text-violet-400" />
                  <h2 className="text-xs font-semibold text-slate-300 uppercase tracking-widest">Usuarios más activos (30 días)</h2>
                </div>
                <div className="divide-y divide-slate-800 max-h-72 overflow-y-auto">
                  {data.usuariosMasActivos?.map((u: any, i: number) => (
                    <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                      <span className="text-slate-600 text-xs w-4 shrink-0">#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-200 truncate">{u.nombre}</p>
                        <p className="text-[10px] text-slate-500">{u.role}</p>
                      </div>
                      <div className="flex gap-2 text-[10px] shrink-0">
                        <span className="text-white font-bold">{u.totalAcciones}</span>
                        <span className="text-emerald-400">✓{u.confirmaciones}</span>
                        <span className="text-red-400">✗{u.cancelaciones}</span>
                        <span className="text-indigo-400">🔑{u.checkins}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Fila 4: Últimos logins ── */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
                <Users className="size-4 text-violet-400" />
                <h2 className="text-xs font-semibold text-slate-300 uppercase tracking-widest">Usuarios — últimas sesiones</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500 border-b border-slate-800">
                      <th className="px-4 py-2 text-left font-medium">Usuario</th>
                      <th className="px-4 py-2 text-left font-medium">Email</th>
                      <th className="px-4 py-2 text-left font-medium">Rol</th>
                      <th className="px-4 py-2 text-left font-medium">Último ingreso</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {data.usuariosActivos?.map((u: any, i: number) => (
                      <tr key={i} className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-200 font-medium">{u.fullName}</td>
                        <td className="px-4 py-2 text-slate-400">{u.email}</td>
                        <td className="px-4 py-2">
                          <span className="px-2 py-0.5 bg-slate-800 rounded text-slate-400">{u.role}</span>
                        </td>
                        <td className="px-4 py-2 text-slate-500">{fmtDate(u.ultimoLogin)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer info */}
            <div className="text-center text-xs text-slate-600 pb-4">
              Panel de diagnóstico — solo visible para superAdmin · {data.timestamp ? fmtDate(data.timestamp) : ""}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
